/**
 * The `auth.get_current_user` payload and its parser, extracted from `useMasters.ts` so the
 * session bridge (`frappeSession.ts`) can reuse it without pulling React Query into the
 * transport layer. `auth.session_from_clerk` returns the identical shape by design — see the
 * spec's §5.1 — so both paths share exactly one parser.
 */

import { str } from './mappers/coerce'
import type { Role } from '../types'

export interface CurrentUser {
  /** The Frappe `User` docname — an email. This is what `purchaser`/`sales_person` Links want. */
  email: string
  full_name: string
  /** Already mapped to Bosun's vocabulary server-side. `null` when the server sent something
   * outside our three roles — never coerced, so an unexpected value cannot silently grant rights. */
  role: Role | null
  /**
   * The companies this user may work under — `auth.get_current_user`'s `company` list
   * (2026-09-19). Settings offers exactly these, and fills the operating company from here
   * when there is only one to choose.
   */
  companies: string[]
}

const BOSUN_ROLES: readonly Role[] = ['sales', 'purchaser', 'purchase_manager']

interface WireCurrentUser {
  email?: unknown
  full_name?: unknown
  role?: unknown
  /** Every Frappe role on the account — see `roleFromFrappeRoles`. */
  frappe_roles?: unknown
  /** `[{name, company_name}]` — the user's companies. */
  company?: unknown
}

/**
 * `frappe_roles` → our vocabulary, HIGHEST first.
 *
 * `auth.get_current_user` answers a single flat `role`, and it discards precedence: an account
 * holding both `Bosun Purchaser` and `Bosun Purchase Manager` is answered as `"purchaser"`
 * (measured 2026-09-06 on two separate accounts, each carrying both). The manager then loses every
 * manager-only control — reassigning a purchaser among them.
 *
 * The same payload lists all the roles, so the precedence is recoverable here. `null` when none of
 * the three is present, which leaves the flat `role` to stand.
 */
export function roleFromFrappeRoles(roles: unknown): Role | null {
  if (!Array.isArray(roles)) return null
  const names = new Set(roles.filter((r): r is string => typeof r === 'string'))
  if (names.has('Bosun Purchase Manager')) return 'purchase_manager'
  if (names.has('Bosun Purchaser')) return 'purchaser'
  if (names.has('Bosun Sales')) return 'sales'
  return null
}

/** Highest wins, so neither source can quietly demote the other. */
const RANK: Record<Role, number> = { sales: 0, purchaser: 1, purchase_manager: 2 }

/** `null` when there is no usable email — a caller must then omit the field rather than send a
 * name the server will reject, which is the bug this line of work started on. */
export function parseCurrentUser(wire: unknown): CurrentUser | null {
  const w = wire as WireCurrentUser | null
  const email = str(w?.email)
  if (email === '') return null
  const stated =
    typeof w?.role === 'string' && (BOSUN_ROLES as string[]).includes(w.role) ? (w.role as Role) : null
  /*
    The two sources are RECONCILED rather than one trusted over the other: `frappe_roles` fixes the
    server's lost precedence, and keeping `stated` when it ranks higher means a server that starts
    answering correctly is never downgraded by a role list we failed to recognise.
  */
  const fromRoles = roleFromFrappeRoles(w?.frappe_roles)
  const role =
    stated === null ? fromRoles : fromRoles === null ? stated : RANK[fromRoles] > RANK[stated] ? fromRoles : stated

  const companies = Array.isArray(w?.company)
    ? w.company.map((c) => str((c as { name?: unknown } | null)?.name)).filter((n) => n !== '')
    : []
  return { email, full_name: str(w?.full_name), role, companies }
}
